import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import GameCard from '@/components/feature/GameCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getGamesByCategory, games } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'hanhua');
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const categoryData = getCategoryBySlug(activeSlug);
  const categoryGames = getGamesByCategory(activeSlug);

  let sortedGames = [...categoryGames];
  if (sortBy === 'rating') {
    sortedGames.sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'downloads') {
    sortedGames.sort((a, b) => b.downloads - a.downloads);
  } else if (sortBy === 'newest') {
    sortedGames.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  const categoryMeta = {
    hanhua: {
      title: '汉化版Galgame下载 — Galgame资源网站PC汉化资源专区',
      description: 'Galgame资源网站PC汉化版专区，提供海量汉化Galgame游戏下载，包含CLANNAD、命运石之门、Fate/stay night等经典作品中文版，Windows系统免费下载，更新及时。',
      keywords: 'galgame汉化版下载,汉化galgame资源,galgame中文版,PC版galgame汉化,galgame资源网站pc汉化',
    },
    riwen: {
      title: '日文原版Galgame下载 — Galgame资源网站PC日文原版专区',
      description: 'Galgame资源网站PC日文原版专区，提供原汁原味日文原版Galgame游戏下载，包含最新发售日文原版及经典日文原版作品，支持Windows系统免费下载。',
      keywords: '日文原版galgame下载,日文galgame资源,galgame原版,PC版日文galgame,galgame资源网站pc日文',
    },
    buding: {
      title: 'Galgame补丁包下载 — Galgame资源网站PC补丁合集专区',
      description: 'Galgame资源网站PC补丁包专区，提供汉化补丁、修正补丁、免CD补丁等各类Galgame补丁下载，覆盖主流Galgame游戏，Windows系统适用。',
      keywords: 'galgame补丁下载,galgame汉化补丁,galgame修正补丁,galgame免CD补丁,galgame资源网站pc补丁',
    },
    gonglve: {
      title: 'Galgame攻略合集 — Galgame资源网站PC攻略专区',
      description: 'Galgame资源网站PC攻略合集专区，提供全路线攻略、隐藏要素、CG存档等完整Galgame攻略内容，助你完整体验每款Galgame游戏。',
      keywords: 'galgame攻略合集,galgame全路线攻略,galgame隐藏要素,galgame CG存档,galgame资源网站pc攻略',
    },
  };

  const meta = categoryMeta[activeSlug as keyof typeof categoryMeta] || {
    title: `${categoryData?.name || '分类'} — Galgame资源网站PC`,
    description: `Galgame资源网站PC ${categoryData?.name || ''}专区，提供海量${categoryData?.name || ''}Galgame游戏资源，支持Windows系统免费下载。`,
    keywords: `galgame ${categoryData?.name || ''},PC版galgame,galgame资源网站pc`,
  };

  const allCategoryGames = games.filter(g => g.category === activeSlug || Math.random() > 0.3).slice(0, 4);
  const displayGames = sortedGames.length > 0 ? sortedGames : allCategoryGames;

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryData?.name || '分类' },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalPath={`/category/${activeSlug}`}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="py-6 md:py-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {categoryData?.name || '分类'}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">
                {categoryData?.description || '浏览Galgame资源'} · 共 {categoryData?.count || displayGames.length} 款资源
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto flex-nowrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-foreground-900'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3 text-sm">
            <span className="text-foreground-400 whitespace-nowrap">排序：</span>
            {[
              { value: 'default', label: '默认' },
              { value: 'rating', label: '评分最高' },
              { value: 'downloads', label: '下载最多' },
              { value: 'newest', label: '最新发布' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === opt.value
                    ? 'bg-secondary-200 text-secondary-800'
                    : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/70'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6" data-product-shop>
              {displayGames.map((game) => (
                <GameCard key={game.id} {...game} />
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}